import { PoolV2Info } from 'Modules'
import { useCallback, useEffect, useState } from 'react'

import abiPTODistribution from 'abi/abi-pto-distribution.json'
import { SettingEnum } from 'constants/setting'
import { useContractViem } from 'hooks/contract/use-contract-viem'
import { useAppSetting } from 'hooks/setting/use-app-setting'
import { convertPercentContract, convertUnitsContract } from 'utils/contract'
import { logError } from 'utils/logger'

/**
 * PTO v2 - read pool info of a collection from PTODistribution.
 * Returns null while the collection has no pool yet (Off Sale).
 */
export const usePoolInfo = (collectionAddress?: string | null) => {
  const { getSetting } = useAppSetting()

  const ptoDistributionContract = getSetting(SettingEnum.PTO_DISTRIBUTION_CONTRACT)

  const contract = useContractViem(ptoDistributionContract, abiPTODistribution)

  const [poolInfo, setPoolInfo] = useState<PoolV2Info | null>(null)

  const getPoolInfo = useCallback(async () => {
    try {
      if (!contract || !collectionAddress) {
        setPoolInfo(null)
        return
      }

      // Check on-chain (besides poolIds from the API): a collection can have
      // multiple pools, stats are read from the latest one
      const poolIdsRes = await contract.read.getPoolIds([collectionAddress])
      const poolIds = (poolIdsRes || []).map((id: bigint) => Number(id))
      if (!poolIds.length) {
        setPoolInfo(null)
        return
      }
      const poolId = poolIds[poolIds.length - 1]

      const [contractInfo, totalLoanAmountRaised] = await Promise.all([
        contract.read.getContractInfo([poolId]),
        contract.read.totalLoanAmountRaised([poolId]),
      ])

      // getContractInfo returns [usdcToken, poolParams, phases, totalNFTsPurchase]
      const poolParams = contractInfo?.[1]
      const totalNFTsPurchase = contractInfo?.[3]

      setPoolInfo({
        poolId,
        poolIds,
        isEnd: Boolean(poolParams?.isEnd),
        buyingFeeBP: convertPercentContract(poolParams?.buyingFeeBP || 0),
        maxNFTAmount: Number(poolParams?.maxNFTAmount || 0),
        totalNFTsPurchase: Number(totalNFTsPurchase || 0),
        totalLoanAmountRaised: convertUnitsContract(totalLoanAmountRaised),
      })
    } catch (error) {
      setPoolInfo(null)
      logError('error get pool info v2-----------------', error)
    }
  }, [contract, collectionAddress])

  useEffect(() => {
    getPoolInfo()
  }, [getPoolInfo])

  return {
    poolInfo,
    getPoolInfo,
  }
}
